import { ApiError, apiRequest, apiUpload } from './client'
import { readInt, readOptionalInt, readString } from './json'
import type {
  CatalogImportConfirmResultDto,
  CatalogImportPreviewDto,
  CatalogImportPreviewRowsDto,
  CatalogImportPreviewReviewStateDto,
  CatalogImportPreviewSessionDto,
  CatalogImportRowFieldOverride,
  ColumnMapping,
  ExistingItemUpdateMode,
  ImportColumnDto,
  ImportPreviewRowDto,
  MatchCandidateDto,
  MatchPreviewDto,
  ProposedMappingDto,
  SaveCatalogImportPreviewResultDto,
} from './catalogImportTypes'
import { mappingPayload } from './catalogImportTypes'

type Raw = Record<string, unknown>

const readBool = (json: Raw, camel: string, snake: string): boolean =>
  Boolean(json[camel] ?? json[snake] ?? false)

const readIntList = (json: Raw, camel: string, snake: string): number[] => {
  const raw = json[camel] ?? json[snake]
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.map((value) => Number(value)).filter((value) => Number.isFinite(value))
}

const readNumberMap = (raw: unknown): Record<number, number> => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {}
  }
  const mapped: Record<number, number> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const row = Number(key)
    const parsed = Number(value)
    if (Number.isFinite(row) && Number.isFinite(parsed)) {
      mapped[row] = parsed
    }
  }
  return mapped
}

const readFieldOverrideMap = (raw: unknown): Record<number, CatalogImportRowFieldOverride> => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {}
  }
  const mapped: Record<number, CatalogImportRowFieldOverride> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const row = Number(key)
    if (!Number.isFinite(row) || !value || typeof value !== 'object') {
      continue
    }
    const json = value as Raw
    mapped[row] = {
      name: readString(json, 'name', 'name') ?? undefined,
      brand: readString(json, 'brand', 'brand') ?? undefined,
      price: readString(json, 'price', 'price') ?? undefined,
      country: readString(json, 'country', 'country') ?? undefined,
      unit: readString(json, 'unit', 'unit') ?? undefined,
      minShipmentQty: readString(json, 'minShipmentQty', 'min_shipment_qty') ?? undefined,
      packageSize: readString(json, 'packageSize', 'package_size') ?? undefined,
      comment: readString(json, 'comment', 'comment') ?? undefined,
    }
  }
  return mapped
}

const fieldOverridesPayload = (
  overrides: Record<number, CatalogImportRowFieldOverride>,
): Record<string, Record<string, string>> => {
  const rowFieldOverrides: Record<string, Record<string, string>> = {}
  for (const [row, override] of Object.entries(overrides)) {
    const payload: Record<string, string> = {}
    if (override.name != null) payload.name = override.name
    if (override.brand != null) payload.brand = override.brand
    if (override.price != null) payload.price = override.price
    if (override.country != null) payload.country = override.country
    if (override.unit != null) payload.unit = override.unit
    if (override.minShipmentQty != null) payload.min_shipment_qty = override.minShipmentQty
    if (override.packageSize != null) payload.package_size = override.packageSize
    if (override.comment != null) payload.comment = override.comment
    if (Object.keys(payload).length > 0) {
      rowFieldOverrides[row] = payload
    }
  }
  return rowFieldOverrides
}

export type CatalogImportTarget = 'supplier' | 'restaurant-my-prices'

const importApiBase = (target: CatalogImportTarget): string =>
  target === 'restaurant-my-prices'
    ? '/buyer/private-catalog/import'
    : '/supplier/catalog/import'

const normalizeColumn = (json: Raw): ImportColumnDto => ({
  index: Number(json.index ?? json.columnIndex ?? 0),
  header: readString(json, 'header', 'columnHeader') ?? '',
  sampleValues: ((json.sampleValues as unknown[] | undefined) ?? []).map(String),
})

const normalizeProposedMapping = (json: Raw): ProposedMappingDto => ({
  field: (json.field as string) ?? '',
  columnIndex:
    json.columnIndex != null
      ? Number(json.columnIndex)
      : json.column_index != null
        ? Number(json.column_index)
        : null,
  columnHeader: readString(json, 'columnHeader', 'column_header'),
  confidence: json.confidence != null ? Number(json.confidence) : null,
  source: (json.source as string | null) ?? null,
  required: (json.required as boolean) ?? false,
})

const normalizeCandidate = (json: Raw): MatchCandidateDto => ({
  productTypeId: readInt(json, 'productTypeId', 'product_type_id'),
  code: readString(json, 'code', 'code') ?? '',
  name: readString(json, 'name', 'name') ?? '',
  score: Number(json.score ?? 0),
})

const normalizeMatchPreview = (json: Raw): MatchPreviewDto => ({
  matcherVersion: readString(json, 'matcherVersion', 'matcher_version') ?? '',
  decision: readString(json, 'decision', 'decision') ?? 'unmatched',
  candidates: ((json.candidates as Raw[] | undefined) ?? []).map(normalizeCandidate),
})

const normalizePreviewRow = (json: Raw): ImportPreviewRowDto => ({
  rowNumber: readInt(json, 'rowNumber', 'row_number'),
  name: readString(json, 'name', 'name') ?? '',
  externalSku: readString(json, 'externalSku', 'external_sku'),
  brand: (json.brand as string | null) ?? null,
  country: (json.country as string | null) ?? null,
  unit: (json.unit as string | null) ?? null,
  price: json.price != null ? String(json.price) : null,
  minShipmentQty:
    json.minShipmentQty != null
      ? String(json.minShipmentQty)
      : json.min_shipment_qty != null
        ? String(json.min_shipment_qty)
        : null,
  packageSize: readString(json, 'packageSize', 'package_size'),
  category: (json.category as string | null) ?? null,
  matchPreview: normalizeMatchPreview((json.matchPreview ?? json.match_preview) as Raw),
  previousProductTypeId:
    (json.previousProductTypeId as number | null | undefined) ??
    (json.previous_product_type_id as number | null | undefined) ??
    null,
  previousPrice: readString(json, 'previousPrice', 'previous_price'),
  isUpdate: readBool(json, 'isUpdate', 'is_update'),
  isNew: readBool(json, 'isNew', 'is_new'),
  hasPriceChange: readBool(json, 'hasPriceChange', 'has_price_change'),
  hasMatchingChange: readBool(json, 'hasMatchingChange', 'has_matching_change'),
})

const normalizePreview = (json: Raw): CatalogImportPreviewDto => ({
  previewToken: readString(json, 'previewToken', 'preview_token') ?? '',
  organizationId: readInt(json, 'organizationId', 'organization_id'),
  filename: readString(json, 'filename', 'filename') ?? '',
  columns: ((json.columns as Raw[] | undefined) ?? []).map(normalizeColumn),
  proposedMapping: ((json.proposedMapping as Raw[] | undefined) ?? []).map(
    normalizeProposedMapping,
  ),
  sampleRows: ((json.sampleRows as Raw[] | undefined) ?? (json.sample_rows as Raw[] | undefined) ?? []).map(
    normalizePreviewRow,
  ),
  totalRows: Number(json.totalRows ?? json.total_rows ?? 0),
  warnings: ((json.warnings as unknown[] | undefined) ?? []).map(String),
  savedProfileApplied: (json.savedProfileApplied as boolean) ?? false,
  expiresAt: readString(json, 'expiresAt', 'expires_at'),
  matchingChangeCount: Number(json.matchingChangeCount ?? json.matching_change_count ?? 0),
  matchingChangeCountComplete: readBool(
    json,
    'matchingChangeCountComplete',
    'matching_change_count_complete',
  ),
})

const normalizePreviewRows = (json: Raw): CatalogImportPreviewRowsDto => ({
  rows: ((json.rows as Raw[] | undefined) ?? []).map(normalizePreviewRow),
  offset: Number(json.offset ?? 0),
  limit: Number(json.limit ?? 0),
  totalRows: Number(json.totalRows ?? json.total_rows ?? 0),
  filteredTotalRows: Number(
    json.filteredTotalRows ?? json.filtered_total_rows ?? json.totalRows ?? json.total_rows ?? 0,
  ),
  hasMore: (json.hasMore as boolean) ?? false,
  expiresAt: readString(json, 'expiresAt', 'expires_at'),
  matchingChangeCount: Number(json.matchingChangeCount ?? json.matching_change_count ?? 0),
  matchingChangeCountComplete: readBool(
    json,
    'matchingChangeCountComplete',
    'matching_change_count_complete',
  ),
  matchingChangeRowNumbers: readIntList(json, 'matchingChangeRowNumbers', 'matching_change_row_numbers'),
  importSummaryComplete: readBool(json, 'importSummaryComplete', 'import_summary_complete'),
  priceChangeCount: Number(json.priceChangeCount ?? json.price_change_count ?? 0),
  priceChangeRowNumbers: readIntList(json, 'priceChangeRowNumbers', 'price_change_row_numbers'),
  newItemCount: Number(json.newItemCount ?? json.new_item_count ?? 0),
  newItemRowNumbers: readIntList(json, 'newItemRowNumbers', 'new_item_row_numbers'),
})

const normalizeConfirmResult = (json: Raw): CatalogImportConfirmResultDto => ({
  imported: Number(json.imported ?? 0),
  autoMatched: Number(json.autoMatched ?? json.auto_matched ?? 0),
  suggested: Number(json.suggested ?? 0),
  unmatched: Number(json.unmatched ?? 0),
  updatedExisting: Number(json.updatedExisting ?? json.updated_existing ?? 0),
})

export const importCatalogPreview = async (
  organizationId: number,
  file: File,
  target: CatalogImportTarget = 'supplier',
): Promise<CatalogImportPreviewDto> => {
  const formData = new FormData()
  formData.append('file', file)
  const result = await apiUpload<Raw>(
    `${importApiBase(target)}/preview?organizationId=${organizationId}`,
    formData,
  )
  return normalizePreview(result)
}

export const importCatalogPreviewRows = async (
  options: {
    organizationId: number
    previewToken: string
    columnMapping: ColumnMapping
    offset: number
    limit?: number
    privateSupplierId?: number | null
    scanMatchingChanges?: boolean
    matchingChangesFirst?: boolean
    matchingChangeRowNumbers?: number[]
    filterRowNumbers?: number[]
  },
  target: CatalogImportTarget = 'supplier',
): Promise<CatalogImportPreviewRowsDto> => {
  const result = await apiRequest<Raw>(`${importApiBase(target)}/preview/rows`, {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      preview_token: options.previewToken,
      column_mapping: mappingPayload(options.columnMapping),
      offset: options.offset,
      limit: options.limit ?? 50,
      ...(options.privateSupplierId != null
        ? { private_supplier_id: options.privateSupplierId }
        : {}),
      ...(options.scanMatchingChanges ? { scan_matching_changes: true } : {}),
      ...(options.matchingChangesFirst ? { matching_changes_first: true } : {}),
      ...(options.matchingChangeRowNumbers != null && options.matchingChangeRowNumbers.length > 0
        ? { matching_change_row_numbers: options.matchingChangeRowNumbers }
        : {}),
      ...(options.filterRowNumbers != null
        ? { filter_row_numbers: options.filterRowNumbers }
        : {}),
    },
  })
  return normalizePreviewRows(result)
}

export type ConfirmCatalogImportOptions = {
  organizationId: number
  venueId?: number | null
  previewToken: string
  columnMapping: ColumnMapping
  rowProductTypeOverrides?: Record<number, number>
  /**
   * Верхний кандидат матчера по строке — тот, что оператор видел в превью. Бэк при явном
   * выборе типа матчер уже не гоняет и без этого не отличит «оставил свой тип, потому что
   * матчер согласен» от «оставил вопреки другому предложению».
   */
  rowMatcherSuggestions?: Record<number, number>
  rowFieldOverrides?: Record<number, CatalogImportRowFieldOverride>
  excludedRowNumbers?: number[]
  privateSupplierId?: number | null
  newSupplier?: {
    legalName: string
    ogrn: string
  } | null
  existingItemUpdateMode?: ExistingItemUpdateMode | null
}

const confirmBody = (options: ConfirmCatalogImportOptions): Record<string, unknown> => {
  const rowProductTypeOverrides: Record<string, number> = {}
  for (const [row, typeId] of Object.entries(options.rowProductTypeOverrides ?? {})) {
    rowProductTypeOverrides[row] = typeId
  }

  const rowMatcherSuggestions: Record<string, number> = {}
  for (const [row, typeId] of Object.entries(options.rowMatcherSuggestions ?? {})) {
    rowMatcherSuggestions[row] = typeId
  }

  const rowFieldOverrides = fieldOverridesPayload(options.rowFieldOverrides ?? {})

  return {
    organization_id: options.organizationId,
    ...(options.venueId != null ? { venue_id: options.venueId } : {}),
    preview_token: options.previewToken,
    column_mapping: mappingPayload(options.columnMapping),
    ...(Object.keys(rowProductTypeOverrides).length > 0
      ? { row_product_type_overrides: rowProductTypeOverrides }
      : {}),
    ...(Object.keys(rowMatcherSuggestions).length > 0
      ? { row_matcher_suggestions: rowMatcherSuggestions }
      : {}),
    ...(Object.keys(rowFieldOverrides).length > 0
      ? { row_field_overrides: rowFieldOverrides }
      : {}),
    ...(options.excludedRowNumbers && options.excludedRowNumbers.length > 0
      ? { excluded_row_numbers: options.excludedRowNumbers }
      : {}),
    ...(options.privateSupplierId != null
      ? { private_supplier_id: options.privateSupplierId }
      : {}),
    ...(options.newSupplier
      ? {
          new_supplier: {
            legal_name: options.newSupplier.legalName,
            ogrn: options.newSupplier.ogrn,
          },
        }
      : {}),
    ...(options.existingItemUpdateMode
      ? { existing_item_update_mode: options.existingItemUpdateMode }
      : {}),
  }
}

export const confirmCatalogImport = async (
  options: ConfirmCatalogImportOptions,
  target: CatalogImportTarget = 'supplier',
): Promise<CatalogImportConfirmResultDto> => {
  const result = await apiRequest<Raw>(`${importApiBase(target)}/confirm`, {
    method: 'POST',
    body: confirmBody(options),
  })
  return normalizeConfirmResult(result)
}

export type ImportJobProgress = {
  processedRows: number
  totalRows: number
}

const JOB_POLL_MS = 500

/**
 * «Мои прайсы»: подтверждение в фоновой задаче ядра и опрос хода раз в полсекунды — для
 * «Разобрали N позиций из M» и чтобы длинный прайс не упирался в таймаут одного запроса.
 * Старое ядро без задач (404) — обычное синхронное подтверждение.
 */
export const confirmCatalogImportWithProgress = async (
  options: ConfirmCatalogImportOptions,
  target: CatalogImportTarget,
  onProgress: (progress: ImportJobProgress) => void,
  signal?: AbortSignal,
): Promise<CatalogImportConfirmResultDto> => {
  if (target !== 'restaurant-my-prices') {
    return confirmCatalogImport(options, target)
  }
  let job: Raw
  try {
    job = await apiRequest<Raw>(`${importApiBase(target)}/confirm-jobs`, {
      method: 'POST',
      body: confirmBody(options),
    })
  } catch (startError) {
    if (startError instanceof ApiError && startError.status === 404) {
      return confirmCatalogImport(options, target)
    }
    throw startError
  }
  const jobId = String(job.job_id ?? '')
  for (;;) {
    onProgress({
      processedRows: Number(job.processed_rows ?? 0),
      totalRows: Number(job.total_rows ?? 0),
    })
    if (job.status === 'done') {
      return normalizeConfirmResult((job.result ?? {}) as Raw)
    }
    if (job.status === 'failed') {
      throw new Error(String(job.error_message ?? 'Не удалось разобрать прайс'))
    }
    if (signal?.aborted) {
      throw new DOMException('Import progress polling aborted', 'AbortError')
    }
    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_MS))
    job = await apiRequest<Raw>(`${importApiBase(target)}/confirm-jobs/${encodeURIComponent(jobId)}`)
  }
}

const normalizeReviewState = (json: Raw): CatalogImportPreviewReviewStateDto => ({
  columnMapping: (json.columnMapping ?? json.column_mapping ?? {}) as ColumnMapping,
  rowProductTypeOverrides: readNumberMap(json.rowProductTypeOverrides ?? json.row_product_type_overrides),
  rowFieldOverrides: readFieldOverrideMap(json.rowFieldOverrides ?? json.row_field_overrides),
  excludedRowNumbers: readIntList(json, 'excludedRowNumbers', 'excluded_row_numbers'),
  offset: Number(json.offset ?? 0),
  matchingChangesFirst: readBool(json, 'matchingChangesFirst', 'matching_changes_first'),
  matchingChangeRowNumbers: readIntList(json, 'matchingChangeRowNumbers', 'matching_change_row_numbers'),
  privateSupplierId: readOptionalInt(json, 'privateSupplierId', 'private_supplier_id'),
  savedAt: readString(json, 'savedAt', 'saved_at'),
})

export const getCatalogImportPreviewSession = async (
  organizationId: number,
  target: CatalogImportTarget = 'supplier',
): Promise<CatalogImportPreviewSessionDto> => {
  const result = await apiRequest<Raw>(
    `${importApiBase(target)}/preview/session?organizationId=${organizationId}`,
  )
  const previewJson = (result.preview ?? {}) as Raw
  const reviewJson = (result.review ?? {}) as Raw
  return {
    preview: normalizePreview(previewJson),
    review: normalizeReviewState(reviewJson),
  }
}

export const saveCatalogImportPreview = async (
  options: {
    organizationId: number
    previewToken: string
    columnMapping: ColumnMapping
    rowProductTypeOverrides?: Record<number, number>
    rowFieldOverrides?: Record<number, CatalogImportRowFieldOverride>
    excludedRowNumbers?: number[]
    offset: number
    matchingChangesFirst?: boolean
    matchingChangeRowNumbers?: number[]
    privateSupplierId?: number | null
  },
  target: CatalogImportTarget = 'supplier',
): Promise<SaveCatalogImportPreviewResultDto> => {
  const rowProductTypeOverrides: Record<string, number> = {}
  for (const [row, typeId] of Object.entries(options.rowProductTypeOverrides ?? {})) {
    rowProductTypeOverrides[row] = typeId
  }
  const result = await apiRequest<Raw>(`${importApiBase(target)}/preview/save`, {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      preview_token: options.previewToken,
      column_mapping: mappingPayload(options.columnMapping),
      offset: options.offset,
      ...(Object.keys(rowProductTypeOverrides).length > 0
        ? { row_product_type_overrides: rowProductTypeOverrides }
        : {}),
      ...(() => {
        const payload = fieldOverridesPayload(options.rowFieldOverrides ?? {})
        return Object.keys(payload).length > 0 ? { row_field_overrides: payload } : {}
      })(),
      ...(options.excludedRowNumbers && options.excludedRowNumbers.length > 0
        ? { excluded_row_numbers: options.excludedRowNumbers }
        : {}),
      ...(options.matchingChangesFirst ? { matching_changes_first: true } : {}),
      ...(options.matchingChangeRowNumbers != null && options.matchingChangeRowNumbers.length > 0
        ? { matching_change_row_numbers: options.matchingChangeRowNumbers }
        : {}),
      ...(options.privateSupplierId != null
        ? { private_supplier_id: options.privateSupplierId }
        : {}),
    },
  })
  return {
    previewToken: readString(result, 'previewToken', 'preview_token') ?? options.previewToken,
    expiresAt: readString(result, 'expiresAt', 'expires_at'),
    savedAt: readString(result, 'savedAt', 'saved_at'),
    offset: Number(result.offset ?? options.offset),
  }
}

export const discardCatalogImportPreviewSession = async (
  organizationId: number,
  previewToken: string | null | undefined,
  target: CatalogImportTarget = 'supplier',
): Promise<void> => {
  const params = new URLSearchParams({ organizationId: String(organizationId) })
  if (previewToken) {
    params.set('previewToken', previewToken)
  }
  await apiRequest<void>(`${importApiBase(target)}/preview/session?${params.toString()}`, {
    method: 'DELETE',
  })
}

export const previewCatalogMatch = async (
  organizationId: number,
  name: string,
  brand?: string | null,
): Promise<MatchPreviewDto> => {
  const result = await apiRequest<Raw>(
    `/supplier/catalog/match-preview?organizationId=${organizationId}`,
    {
      method: 'POST',
      body: {
        name,
        ...(brand?.trim() ? { brand: brand.trim() } : {}),
      },
    },
  )
  return normalizeMatchPreview(result)
}
